import React, { createContext, useContext, useState, useCallback } from 'react';
import { Check, XCircle, AlertCircle, Info, X } from 'lucide-react';

const ToastContext = createContext();

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const showToast = useCallback((message, type = 'success', duration = 3000) => {
        const id = Date.now() + Math.random();
        const toast = {
            id,
            message,
            type,
            timestamp: new Date()
        };

        setToasts(prev => [...prev, toast]);

        if (duration > 0) {
            setTimeout(() => {
                setToasts(prev => prev.filter(t => t.id !== id));
            }, duration);
        }

        return id;
    }, []);

    const hideToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const clearAll = useCallback(() => {
        setToasts([]);
    }, []);

    // Auto-cleanup old toasts (failsafe)
    React.useEffect(() => {
        const cleanup = setInterval(() => {
            const now = new Date();
            setToasts(prev => prev.filter(toast => 
                (now - toast.timestamp) < 10000 // Remove toasts older than 10 seconds
            ));
        }, 5000);

        return () => clearInterval(cleanup);
    }, []);

    const value = {
        showToast,
        hideToast,
        clearAll,
        toasts
    };

    return (
        <ToastContext.Provider value={value}>
            {children}
            <ToastContainer toasts={toasts} onHide={hideToast} />
        </ToastContext.Provider>
    );
};

const ToastContainer = ({ toasts, onHide }) => {
    if (toasts.length === 0) return null;

    return (
        <div className="fixed top-4 right-4 z-50 space-y-2">
            {toasts.map((toast) => (
                <Toast key={toast.id} toast={toast} onHide={onHide} />
            ))}
        </div>
    );
};

const Toast = ({ toast, onHide }) => {
    const getIcon = () => {
        switch (toast.type) {
            case 'success': return Check;
            case 'error': return XCircle;
            case 'warning': return AlertCircle;
            case 'info': return Info;
            default: return Check;
        }
    };

    const getStyles = () => {
        switch (toast.type) {
            case 'success': return 'bg-green-500 text-white';
            case 'error': return 'bg-red-500 text-white';
            case 'warning': return 'bg-yellow-500 text-white';
            case 'info': return 'bg-blue-500 text-white';
            default: return 'bg-gray-800 text-white';
        }
    };

    const Icon = getIcon();

    return (
        <div className={`${getStyles()} p-4 rounded-lg shadow-lg max-w-sm animate-slide-in-right`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm font-medium">{toast.message}</span>
                </div>
                <button
                    onClick={() => onHide(toast.id)}
                    className="ml-4 flex-shrink-0 text-white hover:text-gray-200 transition-colors"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
};

// Utility hook for common actions with feedback
export const useActionWithFeedback = () => {
    const { showToast, hideToast } = useToast();

    const executeAction = useCallback(async (action, options = {}) => {
        const {
            loadingMessage = null,
            successMessage = 'Action completed successfully',
            errorMessage = 'Action failed. Please try again.',
            onSuccess = null,
            onError = null
        } = options;

        let loadingToastId = null;

        try {
            if (loadingMessage) {
                loadingToastId = showToast(loadingMessage, 'info', 0); // Persistent until cleared
            }

            const result = await action();

            if (loadingToastId) {
                // Remove loading toast - handled by hideToast
                hideToast(loadingToastId);
            }

            showToast(successMessage, 'success');

            if (onSuccess) {
                onSuccess(result);
            }

            return result;
        } catch (error) {
            if (loadingToastId) {
                // Remove loading toast - handled by hideToast
                hideToast(loadingToastId);
            }

            console.error('Action failed:', error);
            showToast(errorMessage, 'error');

            if (onError) {
                onError(error);
            }

            throw error;
        }
    }, [showToast]);

    return { executeAction };
};

export default ToastProvider;